/* ==========================================================================
   ebc-tjc.js: Shop LC 24K gold zodiac coin EBC, migrated for TJC
   Load once (Product JS metafield, deferred). Safe if included twice.
   - Zodiac carousel: Shop LC's original logic, scoped per section.
   - Review slider: RECONSTRUCTED from the Shop LC page snapshot (the original
     script was not supplied): looping track with one clone at each end,
     active card centred, 0.45 s slide, prev/next, drag/swipe, arrow keys,
     plus autoplay (one slide every 10 s; pauses on hover/focus/drag, off
     screen and in hidden tabs; disabled for reduced motion).
   - Scroll-to-top CTAs (hero + reviews): Shop LC's original behaviour.
   Sections initialise only when they approach the viewport.
   ========================================================================== */
(() => {
  'use strict';

  if (window.__ebcgLoaded) return;
  window.__ebcgLoaded = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Run init(el) once, when el comes within 600px of the viewport. */
  const nearObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          nearObserver.unobserve(entry.target);
          const init = entry.target.__ebcgInit;
          delete entry.target.__ebcgInit;
          if (init) init(entry.target);
        });
      }, { rootMargin: '600px 0px' })
    : null;
  const whenNear = (el, init) => {
    if (el.dataset.ebcgReady) return;
    el.dataset.ebcgReady = '1';
    if (!nearObserver) { init(el); return; }
    el.__ebcgInit = init;
    nearObserver.observe(el);
  };

  /* ------------------------------------------------------------------------
     Zodiac carousel
     ------------------------------------------------------------------------ */
  const SIGNS = [
    { n: 'Aries', el: 'Bold', mode: 'Energetic', ruler: 'Ready to lead', tag: 'Born to move, made to lead.', body: 'Aries brings confidence, energy, and a fearless approach to life. This sign is known for taking the first step and following its own path.' },
    { n: 'Taurus', el: 'Loyal', mode: 'Grounded', ruler: 'Loves the finer things', tag: 'Strength and beauty, perfectly balanced.', body: 'Taurus values comfort, stability, and lasting beauty. Steady at heart, this sign knows what it loves and holds onto it.' },
    { n: 'Gemini', el: 'Curious', mode: 'Clever', ruler: 'Always exploring', tag: 'Two sides. Endless possibilities.', body: 'Gemini is curious, expressive, and always ready for something new. Quick thinking and lively conversation come naturally to this air sign.' },
    { n: 'Cancer', el: 'Intuitive', mode: 'Caring', ruler: 'Deeply connected', tag: 'Guided by heart, inspired by the moon.', body: 'Cancer leads with feeling, loyalty, and a strong sense of home. This intuitive sign treasures the people and memories that matter most.' },
    { n: 'Leo', el: 'Confident', mode: 'Warm', ruler: 'Born to shine', tag: 'A little more light wherever you go.', body: 'Leo brings warmth, confidence, and unmistakable presence. Ruled by the Sun, this sign is naturally drawn to self expression and celebration.' },
    { n: 'Virgo', el: 'Precise', mode: 'Observant', ruler: 'Notices everything', tag: 'Precision and grace, written in the stars.', body: 'Virgo finds beauty in the details others may overlook. Thoughtful, practical, and observant, this sign approaches life with care and purpose.' },
    { n: 'Libra', el: 'Balanced', mode: 'Charming', ruler: 'Drawn to beauty', tag: 'Harmony looks good on you.', body: 'Libra is drawn to balance, beauty, and meaningful connection. With Venus as its ruler, this sign has a natural appreciation for elegance and harmony.' },
    { n: 'Scorpio', el: 'Intense', mode: 'Loyal', ruler: 'Quietly powerful', tag: 'Mystery, depth, and strength within.', body: 'Scorpio is known for depth, determination, and powerful intuition. This water sign feels deeply and rarely does anything halfway.' },
    { n: 'Sagittarius', el: 'Adventurous', mode: 'Optimistic', ruler: 'Always looking ahead', tag: 'Made for the journey ahead.', body: 'Sagittarius is curious about the world and always ready to explore. Freedom, discovery, and a positive outlook define this adventurous fire sign.' },
    { n: 'Capricorn', el: 'Ambitious', mode: 'Steady', ruler: 'Built for success', tag: 'Quiet ambition. Lasting strength.', body: 'Capricorn believes in patience, purpose, and putting in the work. Grounded and determined, this sign keeps moving steadily toward its goals.' },
    { n: 'Aquarius', el: 'Original', mode: 'Independent', ruler: 'Thinks differently', tag: 'Different by nature, original by choice.', body: 'Aquarius looks at the world from a fresh perspective. Independent and imaginative, this sign is comfortable following ideas others may not see yet.' },
    { n: 'Pisces', el: 'Dreamy', mode: 'Compassionate', ruler: 'Deeply intuitive', tag: 'A world of feeling, imagination, and wonder.', body: 'Pisces brings sensitivity, imagination, and a strong intuitive nature. This water sign often sees beauty and meaning where others might miss it.' },
  ];
  const QUAL = [
    'Brave · Direct · first to move', 'Steady · Sensual · built to last', 'Quick · Curious · two minds at once',
    'Loyal · Intuitive · keeps its people close', 'Warm · Generous · impossible to miss', 'Precise · Observant · notices everything',
    'Fair · Charming · weighs both sides', 'Intense · Private · remembers all of it', 'Restless · Honest · always aimed outward',
    'Patient · Ambitious · plays the long game', 'Original · Independent · thinks ahead', 'Dreamy · Gentle · feels it first',
  ];
  // Same date logic as the original, including its January/February overrides.
  function currentSign() {
    const now = new Date();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const dates = [[3, 21], [4, 20], [5, 21], [6, 21], [7, 23], [8, 23], [9, 23], [10, 23], [11, 22], [12, 22], [1, 20], [2, 19]];
    let current = 9;
    dates.forEach(([m, d], i) => { if (month > m || (month === m && day >= d)) current = i; });
    if (month === 1 && day < 20) current = 10;
    if (month === 2 && day < 19) current = 11;
    return current;
  }

  function initZodiac(root) {
    const deck = root.querySelector('[data-zodiac-deck]');
    if (!deck) return;
    const cards = [...deck.querySelectorAll('.ebcg-zodiac__card')];
    const traits = root.querySelector('[data-zodiac-traits]');
    const qual = root.querySelector('[data-zodiac-qual]');
    const tag = root.querySelector('[data-zodiac-tag]');
    const body = root.querySelector('[data-zodiac-body]');
    let active = currentSign();
    let spread = 1;
    let lastWidth = -1;

    const measure = () => { spread = Math.max(76, Math.min(178, (deck.clientWidth || 1100) * 0.138)); };

    function setActive(index) {
      active = ((index % 12) + 12) % 12;
      cards.forEach((card, i) => {
        let offset = i - active;
        if (offset > 6) offset -= 12;
        if (offset < -6) offset += 12;
        const distance = Math.abs(offset);
        card.classList.toggle('is-side', offset !== 0);
        card.classList.toggle('is-far', distance > 4);
        card.classList.toggle('is-edge', distance === 4);
        card.style.transform = `translateX(${(offset * spread).toFixed(1)}px) translateY(${(distance * distance * 3.5).toFixed(1)}px) translateZ(${-distance * 66}px) rotateY(${offset * -15}deg) scale(${(1 - distance * 0.045).toFixed(3)})`;
        card.style.zIndex = String(20 - distance);
      });
      const sign = SIGNS[active];
      if (qual) qual.textContent = QUAL[active];
      if (traits) {
        traits.replaceChildren(...[sign.el, sign.mode, sign.ruler].map((text) => {
          const span = document.createElement('span');
          span.className = 'ebcg-zodiac__trait';
          span.textContent = text;
          return span;
        }));
      }
      if (tag) tag.textContent = sign.tag;
      if (body) body.textContent = sign.body;
    }

    // Card click / Enter / Space (one delegated listener each).
    deck.addEventListener('click', (event) => {
      const card = event.target.closest('.ebcg-zodiac__card');
      if (card) setActive(cards.indexOf(card));
    });
    deck.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { setActive(active + 1); event.preventDefault(); return; }
      if (event.key === 'ArrowLeft') { setActive(active - 1); event.preventDefault(); return; }
      if (event.key === 'Enter' || event.key === ' ') {
        const card = event.target.closest('.ebcg-zodiac__card');
        if (card) { setActive(cards.indexOf(card)); event.preventDefault(); }
      }
    });
    root.querySelector('[data-zodiac-prev]')?.addEventListener('click', () => setActive(active - 1));
    root.querySelector('[data-zodiac-next]')?.addEventListener('click', () => setActive(active + 1));

    // Touch swipe (passive), 40px threshold as in the original.
    let touchStartX = null;
    deck.addEventListener('touchstart', (event) => { touchStartX = event.touches[0].clientX; }, { passive: true });
    deck.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const deltaX = event.changedTouches[0].clientX - touchStartX;
      if (Math.abs(deltaX) > 40) setActive(active + (deltaX < 0 ? 1 : -1));
      touchStartX = null;
    }, { passive: true });

    // Re-fan only when the deck width actually changes (original: window resize + width check).
    const refit = () => {
      const width = deck.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      measure();
      setActive(active);
    };
    if ('ResizeObserver' in window) new ResizeObserver(refit).observe(deck);
    else window.addEventListener('resize', refit, { passive: true });
    refit();
  }

  /* ------------------------------------------------------------------------
     Review slider (reconstructed; see header)
     ------------------------------------------------------------------------ */
  function initReviews(root) {
    const viewport = root.querySelector('[data-slider-viewport]');
    const track = root.querySelector('[data-slider-track]');
    if (!viewport || !track) return;
    const slides = [...track.querySelectorAll('[data-slide]')];
    const count = slides.length;
    if (count < 2) return;

    // One clone at each end, as in the snapshot (last before first, first after last).
    const makeClone = (slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('data-slide-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'lazy'));
      return clone;
    };
    track.prepend(makeClone(slides[count - 1]));
    track.append(makeClone(slides[0]));
    const all = [...track.children];

    let index = 1;           // position in `all`; 1 = first real slide
    let step = 0;            // card width + gap
    let centre = 0;          // offset that centres a card in the viewport
    let dragStartX = null;
    let dragDelta = 0;

    // Autoplay: one slide every 10 s. Paused while hovered, focused, dragged,
    // off screen or in a hidden tab; off for prefers-reduced-motion.
    const AUTOPLAY_MS = 10000;
    let timer = 0;
    let hovered = false;
    let focused = false;
    let inView = false;
    const stopAutoplay = () => { clearTimeout(timer); timer = 0; };
    const schedule = () => {
      stopAutoplay();
      if (reduceMotion.matches || hovered || focused || dragStartX !== null || !inView || document.hidden) return;
      timer = setTimeout(() => go(1), AUTOPLAY_MS);
    };

    const measure = () => {
      const cardWidth = all[0].getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      step = cardWidth + gap;
      centre = (viewport.clientWidth - cardWidth) / 2;
    };
    const render = (animate) => {
      track.classList.toggle('is-animating', animate && !reduceMotion.matches);
      track.style.transform = `translate3d(${(centre - index * step + dragDelta).toFixed(1)}px, 0px, 0px)`;
      const real = (index - 1 + count) % count;
      slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== real)));
    };
    const go = (delta) => {
      if (index === 0 || index === count + 1) { schedule(); return; } // finish the loop jump first
      index += delta;
      render(true);
      if (reduceMotion.matches) settle();
      schedule();
    };
    // After sliding onto a clone, jump without animation to the matching real slide.
    const settle = () => {
      if (index === 0) index = count;
      else if (index === count + 1) index = 1;
      else return;
      render(false);
    };
    track.addEventListener('transitionend', (event) => { if (event.target === track) settle(); });

    root.querySelector('[data-slider-prev]')?.addEventListener('click', () => go(-1));
    root.querySelector('[data-slider-next]')?.addEventListener('click', () => go(1));
    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { go(1); event.preventDefault(); }
      if (event.key === 'ArrowLeft') { go(-1); event.preventDefault(); }
    });

    // Drag / swipe (pointer events cover mouse and touch; vertical scrolling stays native via touch-action: pan-y).
    viewport.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      dragStartX = event.clientX;
      dragDelta = 0;
      stopAutoplay();
      viewport.setPointerCapture(event.pointerId);
    });
    viewport.addEventListener('pointermove', (event) => {
      if (dragStartX === null) return;
      dragDelta = event.clientX - dragStartX;
      render(false);
    });
    const endDrag = () => {
      if (dragStartX === null) return;
      const delta = dragDelta;
      dragStartX = null;
      dragDelta = 0;
      if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
      else { render(true); schedule(); }
    };
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    // A drag must not follow a link or select text.
    viewport.addEventListener('dragstart', (event) => event.preventDefault());

    const slider = viewport.closest('.ebcg-reviews__slider') || root;
    slider.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') { hovered = true; stopAutoplay(); } });
    slider.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') { hovered = false; schedule(); } });
    slider.addEventListener('focusin', () => { focused = true; stopAutoplay(); });
    slider.addEventListener('focusout', (event) => { if (!slider.contains(event.relatedTarget)) { focused = false; schedule(); } });
    document.addEventListener('visibilitychange', schedule);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; schedule(); }, { threshold: 0.25 }).observe(slider);
    }

    const refit = () => { measure(); render(false); };
    if ('ResizeObserver' in window) new ResizeObserver(refit).observe(viewport);
    else window.addEventListener('resize', refit, { passive: true });
    refit();
  }

  /* ------------------------------------------------------------------------
     Scroll-to-top CTAs (one delegated listener, as in the original)
     ------------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const cta = event.target instanceof Element
      ? event.target.closest('a[data-scroll-to-top].ebcg-showcase__cta, a[data-scroll-to-top].ebcg-reviews__cta')
      : null;
    if (!cta) return;
    event.preventDefault();
    window.scrollTo({ top: 0, left: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  });

  /* ------------------------------------------------------------------------
     Boot, theme-editor re-renders and TJC lazy containers
     ------------------------------------------------------------------------ */
  const boot = (scope) => {
    scope.querySelectorAll('[data-ebcg-zodiac]').forEach((el) => whenNear(el, initZodiac));
    scope.querySelectorAll('[data-ebcg-reviews]').forEach((el) => whenNear(el, initReviews));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  else boot(document);
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) boot(event.target);
  });
  // TJC's lazy CX containers (assets/lazy-content-section.js) insert their
  // content after page load and then fire this bubbling event; start any
  // carousel or slider that arrived with it.
  document.addEventListener('lazy-content-section:loaded', (event) => {
    if (event.target instanceof Element) boot(event.target);
  });
})();